import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { RelativeTime } from '@/components/ui/relative-time'
import { ProcessingIndicator } from '@/components/conversations/ProcessingIndicator'
import { getInitials } from '@/lib/utils'
import type { FDEMessage } from '@/types/fde'

const REMARK_PLUGINS = [remarkGfm]
const REHYPE_PLUGINS = [rehypeHighlight]
const SCROLL_THRESHOLD = 150

interface FDEMessageListProps {
  messages: FDEMessage[]
  processing: boolean
  currentActivity: string | null
}

export function FDEMessageList({ messages, processing, currentActivity }: FDEMessageListProps) {
  const { t } = useTranslation('fde')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentEndRef = useRef<HTMLDivElement>(null)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)
  const prevMessagesLengthRef = useRef(messages.length)

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [])

  // Scroll to bottom on initial load
  useEffect(() => {
    scrollToBottom()
  }, [])

  // Listen for scroll events to toggle auto-scroll
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      setAutoScrollEnabled(distanceFromBottom <= SCROLL_THRESHOLD)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (autoScrollEnabled && messages.length > prevMessagesLengthRef.current) {
      scrollToBottom()
    }
    prevMessagesLengthRef.current = messages.length
  }, [messages.length, autoScrollEnabled, scrollToBottom])

  // Auto-scroll during streaming (content changes on last message)
  const lastMessage = messages[messages.length - 1]
  const lastMessageContent = lastMessage?.content
  useEffect(() => {
    if (autoScrollEnabled && processing) {
      scrollToBottom()
    }
  }, [lastMessageContent, autoScrollEnabled, processing, scrollToBottom])

  return (
    <div ref={scrollContainerRef} className="relative flex-1 min-h-0 overflow-y-auto px-4">
      <div className="mx-auto max-w-3xl py-4">
        {messages.map(message => (
          <FDEMessageBubble
            key={message.id}
            message={message}
          />
        ))}

        {processing && (
          <ProcessingIndicator
            hasActivity={!!currentActivity}
            activityText={currentActivity}
          />
        )}

        <div ref={contentEndRef} />
      </div>

      {!autoScrollEnabled && (
        <Button
          onClick={scrollToBottom}
          size="icon"
          variant="secondary"
          className="sticky bottom-4 left-1/2 -translate-x-1/2 rounded-full shadow-md z-10"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

interface FDEMessageBubbleProps {
  message: FDEMessage
}

function FDEMessageBubble({ message }: FDEMessageBubbleProps) {
  const isStreaming = message.status === 'processing'
  const isCompleted = message.status === 'completed'

  if (message.role === 'user') {
    const userName = message.user?.name || 'User'
    const avatarUrl = message.user?.avatar_url
    const initials = getInitials(userName)

    return (
      <div className="flex justify-end gap-3 animate-in fade-in slide-in-from-right-2 duration-300 min-w-0 mt-4">
        <div className="flex flex-col items-end gap-1 max-w-[80%] min-w-0">
          <div className="bg-slate-200 dark:text-foreground dark:bg-slate-800 border border-primary/10 rounded-lg px-3 py-2 min-w-0">
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
          {isCompleted && (
            <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
          )}
        </div>
        <Avatar className="h-7 w-7 flex-shrink-0">
          <AvatarImage src={avatarUrl || undefined} alt={userName} />
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium border border-primary/20">
            {initials}
          </AvatarFallback>
        </Avatar>
      </div>
    )
  }

  if (message.role === 'assistant') {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
        <div className="flex flex-col gap-1 min-w-0 flex-1 max-w-full">
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm [overflow-wrap:anywhere]">
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={isStreaming ? undefined : REHYPE_PLUGINS}
            >
              {message.content || ''}
            </ReactMarkdown>
          </div>

          {isCompleted && (
            <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
          )}
        </div>
      </div>
    )
  }

  return null
}
